import React from "react";
import {
  Award, TrendingUp, XCircle, CheckCircle2, MessageSquareQuote,
  AlertTriangle, ShieldCheck,
} from "lucide-react";

/**
 * Renders the GENUINE evaluation returned by the backend
 * (/api/demo/interview/score). No fabricated numbers.
 *
 * props:
 *   result       — backend payload: { ok, overall, raw, answered, expected,
 *                  recommendation, summary, competencies[], strengths[], improvements[] }
 *                  or { ok:false } / null when scoring was unavailable.
 *   conversation — [{ role: 'assistant'|'user', text }] realtime transcript.
 */
export default function DemoResult({ result, conversation = [] }) {
  const scored = result && result.ok;

  const overall = scored ? result.overall : null;
  const scoreColor =
    overall == null ? "text-muted-foreground"
      : overall >= 80 ? "text-success"
      : overall >= 65 ? "text-primary"
      : overall >= 50 ? "text-foreground"
      : "text-warning";

  const recBadge =
    result?.recommendation === "Strong Hire" ? "bg-success/15 text-success border-success/30"
      : result?.recommendation === "Hire" ? "bg-primary/15 text-primary border-primary/30"
      : result?.recommendation === "Lean Hire" ? "bg-warning/15 text-warning border-warning/30"
      : "bg-muted text-muted-foreground border-border";

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      {/* Score / recommendation */}
      <div className="rounded-2xl border border-border bg-card p-6 flex flex-col items-center justify-center text-center">
        {scored ? (
          <>
            <div className="text-xs font-semibold text-muted-foreground tracking-wide mb-3">OVERALL SCORE</div>
            <div className={`text-6xl font-extrabold ${scoreColor}`}>{overall}</div>
            <div className="text-muted-foreground text-sm mb-4">/ 100</div>
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-semibold ${recBadge}`}>
              <Award size={14} /> {result.recommendation}
            </span>
            <p className="text-xs text-muted-foreground mt-4">
              Based on {result.answered} of {result.expected} question{result.expected === 1 ? "" : "s"} answered.
            </p>
          </>
        ) : (
          <>
            <AlertTriangle size={32} className="text-warning mb-3" />
            <div className="font-semibold text-foreground">AI scoring unavailable</div>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
              We couldn't reach the evaluation service just now, so we're not showing a score
              rather than a made-up one. Your full transcript is below.
            </p>
          </>
        )}
      </div>

      {/* Summary + competencies */}
      <div className="rounded-2xl border border-border bg-card p-5 lg:col-span-2">
        {scored ? (
          <>
            {result.summary && (
              <>
                <div className="text-sm font-semibold text-foreground mb-1">AI summary</div>
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{result.summary}</p>
              </>
            )}
            {result.competencies?.length > 0 && (
              <>
                <div className="text-sm font-semibold text-foreground mb-3">Competency breakdown</div>
                <div className="space-y-3">
                  {result.competencies.map((c, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-foreground">{c.name}</span>
                        <span className="font-semibold text-foreground">{c.score}</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className={`h-full ${c.score >= 80 ? "bg-success" : c.score >= 60 ? "bg-primary" : "bg-warning"}`}
                          style={{ width: `${c.score}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="h-full flex items-center gap-2 text-sm text-muted-foreground">
            <ShieldCheck size={16} className="text-primary" />
            Sign up to run real, scored interviews with candidates — evaluation, transcript and integrity checks included.
          </div>
        )}
      </div>

      {/* Strengths */}
      {scored && result.strengths?.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-success text-sm font-semibold mb-3">
            <TrendingUp size={16} /> Strengths
          </div>
          <ul className="space-y-2">
            {result.strengths.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                <CheckCircle2 size={16} className="text-success shrink-0 mt-0.5" /> {s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Improvements */}
      {scored && result.improvements?.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-warning text-sm font-semibold mb-3">
            <XCircle size={16} /> Areas to improve
          </div>
          <ul className="space-y-2">
            {result.improvements.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                <XCircle size={16} className="text-warning shrink-0 mt-0.5" /> {s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Transcript */}
      {conversation.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5 lg:col-span-3">
          <div className="flex items-center gap-2 text-foreground text-sm font-semibold mb-3">
            <MessageSquareQuote size={16} className="text-primary" /> Interview transcript
          </div>
          <div className="space-y-3">
            {conversation.map((m, i) => (
              <div key={i} className={m.role === "assistant" ? "" : "text-right"}>
                <div className="text-[11px] text-muted-foreground mb-0.5">
                  {m.role === "assistant" ? "Interviewer" : "You"}
                </div>
                <div className={`inline-block px-3 py-2 rounded-lg text-sm leading-relaxed max-w-[90%] ${
                  m.role === "assistant" ? "bg-secondary/60 text-foreground" : "bg-primary text-primary-foreground"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
          </div>
          {scored && (
            <p className="text-[11px] text-muted-foreground mt-4">
              Evaluated by SearchJob.ai's AI from your actual answers · practice demo.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
